import { useEffect, useState } from 'react';
import type { ImgHTMLAttributes } from 'react';

import { requestBlob } from '../../api/client';
import { apiBase, isNative } from '../../platform/host';

/**
 * A picture drawn by the API: an uploaded photograph, the QR code for enrolling
 * a second factor, the bitmap a label will be printed as.
 *
 * None of them are public files — every one needs the session — which is why
 * none of them is a plain `<img src="/media/...">`. In a browser that costs
 * nothing: the cookie rides along with the image request like any other, and the
 * address is used directly.
 *
 * The application cannot do that. Its session is a bearer token, and there is no
 * way to put a header on an `<img>`: the browser fetches that URL by itself,
 * with whatever it has, which here is nothing. So the picture is fetched the
 * same way as every other request and handed to the tag as an object URL.
 *
 * Revoked on the way out. A gallery of forty thumbnails that never released them
 * would hold forty full images in memory for as long as the screen is open.
 */

interface Props extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'src'> {
  /** Path under `/api/v1`, query and all. */
  path: string;
  alt: string;
}

export function ApiImage({ path, alt, ...rest }: Props) {
  const [src, setSrc] = useState(() => (isNative ? '' : apiBase + path));

  useEffect(() => {
    if (!isNative) {
      setSrc(apiBase + path);
      return;
    }

    let url: string | null = null;
    let cancelled = false;

    void requestBlob(path)
      .then((blob) => {
        if (cancelled) return;
        url = URL.createObjectURL(blob);
        setSrc(url);
      })
      .catch(() => {
        // A picture that will not load is a missing picture, not a broken
        // screen. The `alt` text is already the answer.
        if (!cancelled) setSrc('');
      });

    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [path]);

  return <img src={src} alt={alt} {...rest} />;
}

interface FileProps extends Omit<Props, 'path'> {
  fileId: string;
  variant?: 'thumb' | 'md' | 'lg';
}

/** An uploaded photograph, by the identifier of the stored file. */
export function FileImage({ fileId, variant, ...rest }: FileProps) {
  return (
    <ApiImage
      path={`/files/${fileId}/content${variant ? `?variant=${variant}` : ''}`}
      {...rest}
    />
  );
}
